import {
  useRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useReducer,
} from "react";
import { Badge, Pagination, Table } from "rsuite";
import { ApiAppointment } from "../../core/apis";
import { ApiSchemaAppointment } from "../../models/api/appointment";
import { useHistory } from "react-router-dom";
import { AppRouteUi } from "../../config/appRoute";
import { useAppSelector } from "../../core/redux";
import { useAppDispatch } from "../../core/redux";
import { ActionsAppointmentTable } from "../../core/redux/slices/sliceAppointmentTable";
import {
  TextCell,
  DateCell,
  ActionCell,
  SeeTasksCell,
} from "../../components/tableItem.component";
import { useAlertDialog } from "../../components/alertDialog";
import { sortTableData } from "../../utils/tableFilter";
import { BooleanToggleFilterIcon } from "../../components/booleanToggleFilterIcon.component";
import { showToast } from "../../components/toast.component";
import { AppointmentStatusCell } from "../../components/appointmentStatusCell";
import { TableHeader } from "../../components";
import { UtilityDate } from "../../utils/dateUtils";
import { FiEdit, FiTrash2, FiFileText } from "react-icons/fi";
import {
  FaCog,
  FaRegCalendar,
  FaCalendarWeek,
  FaRegCalendarAlt,
} from "react-icons/fa";

const { Column, HeaderCell, Cell } = Table;

type TableData = Omit<ApiSchemaAppointment, "maxHours"> & {
  checked?: boolean;
  maxHours: string;
};

type TypeTableKey = keyof TableData;

type AppointmentListProps = {
  isValidating?: boolean;
  data?: ApiSchemaAppointment[];
  totalCount: number;
  filterOptions?: {
    startDate?: Date | null;
    endDate?: Date | null;
    selectedTasks: string[];
    appointmentStatus?: string;
  };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BadgeCell = ({
  rowData,
  dataKey,
  ...props
}: {
  rowData?: TableData;
  dataKey?: keyof TableData;
}) => {
  const getColor = (status: string) =>
    status === "Complete"
      ? "#808080"
      : status === "Upcoming"
      ? "#00BDB4"
      : "#FF0000";

  return (
    <Cell {...props}>
      {rowData && dataKey && (
        <Badge
          content={rowData[dataKey]}
          style={{ background: getColor(rowData[dataKey] as string) }}
          className="p-1 px-2"
        />
      )}
    </Cell>
  );
};

export const AppointmentList = ({
  data,
  isValidating,
  totalCount,
}: AppointmentListProps) => {
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [selected, setSelected] = useState<TableData[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const triggerRef = useRef<any>();
  const routerHistory = useHistory();
  const { alertDialogCtrl, alertDialogComponent } = useAlertDialog();
  const { pageNumber, pageSize, isLoading } = useAppSelector(
    (state) => state.appointmentTable
  );
  const dispatch = useAppDispatch();

  const [sortBy, setSortBy] = useState<TypeTableKey>("" as TypeTableKey);
  const [sortAsc, setSortAsc] = useState(true);
  const [filterAutoClockCheckIn, toggleFilterAutoClockCheckIn] = useReducer(
    (curr: number) => (curr + 1) % 3,
    2
  );

  useEffect(
    () =>
      setTableData(
        data?.map((appointment) => ({
          ...appointment,
          maxHours: Number(appointment.maxHours).toFixed(2),
          patientProfilePicture: appointment.patientProfilePic,
          patientName: appointment.patientName,
          patientPhone: appointment.patientPhoneNumber,
          careGiverName: appointment.caregiverName,
          startTime: new Date(
            0,
            0,
            0,
            new Date(appointment.taskStartTime).getHours(),
            new Date(appointment.taskStartTime).getMinutes(),
            new Date(appointment.taskStartTime).getSeconds()
          ),
        })) ?? []
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  const filteredTableData = useMemo(() => {
    return sortTableData(tableData, sortBy, sortAsc);
  }, [tableData, sortAsc, sortBy]);

  const CellRowSelection = useMemo((): ReactNode => {
    return (rowData: TableData) => (
      <input
        type="checkbox"
        checked={!!rowData.checked}
        onChange={(ev) =>
          rowData.sKey &&
          setTableData((tableData) =>
            tableData?.map((td) =>
              td.sKey === rowData.sKey
                ? {
                    ...td,
                    checked: ev.target.checked,
                  }
                : td
            )
          )
        }
      />
    );
  }, []);

  const selectAllRow = useCallback((checked: boolean) => {
    setTableData((tableData) =>
      tableData.map((td) => ({
        ...td,
        checked: checked,
      }))
    );
  }, []);

  const isAllChecked = useMemo(
    () => tableData.length > 0 && tableData.every((td) => !!td.checked),
    [tableData]
  );

  useEffect(() => {
    setSelected(tableData.filter((td) => !!td.checked));
  }, [tableData]);

  const onDelete = useCallback(
    () =>
      alertDialogCtrl.open({
        message: "Are you sure you want to delete the selected appointments?",
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          setIsDeleting(true);
          dispatch(ActionsAppointmentTable.setIsLoading(true));
          await Promise.all(
            selected.map(
              async (item) => item.sKey && (await deleteItem(item.sKey))
            )
          );
          dispatch(ActionsAppointmentTable.setIsLoading(false));
          dispatch(ActionsAppointmentTable.setRefetch(true));
          setIsDeleting(false);
        },
      }),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected]
  );

  const handleSelectMenu = async (
    eventKey: string,
    _event?: any, // Changed Event to any and made it optional
    sKey?: string
  ) => {
    if (eventKey === "delete" && sKey)
      alertDialogCtrl.open({
        message: "Are you sure you want to delete this appointment?",
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          dispatch(ActionsAppointmentTable.setIsLoading(true));
          await deleteItem(sKey);
          dispatch(ActionsAppointmentTable.setIsLoading(false));
        },
      });
    else if (eventKey === "update" && sKey)
      if (
        filteredTableData.filter((td) => td.sKey === sKey)[0].status ===
        "Complete"
      ) {
        showToast("This appointment is already completed", "error");
      } else routerHistory.push(AppRouteUi.Appointment.UpdateById(sKey));
  };

  const deleteItem = async (sKey: string) => {
    const deleted = await ApiAppointment.Delete(sKey);
    const updAction = async (res?: ApiSchemaAppointment[]) => {
      return res?.filter((i) => i.sKey === sKey) || [];
    };
    if (deleted) ApiAppointment.GetAll.Mutate(updAction);
  };

  return (
    <div>
      {alertDialogComponent}
      <TableHeader
        buttonText="Add New Appointment"
        redirectPath={AppRouteUi.Appointment.Add()}
        onDelete={onDelete}
        pageSize={pageSize}
        TableAction={ActionsAppointmentTable}
        isDeleting={isDeleting}
        isSelected={!!selected.length}
      />
      <Table
        autoHeight
        rowClassName="table-row-hover"
        bordered
        cellBordered
        hover
        classPrefix="rs-table"
        className="table-striped"
        data={filteredTableData}
        loading={isValidating || isLoading}
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Appointments Available</h3>
          </div>
        )}
        onScroll={() => triggerRef.current?.close()}
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortBy, sortAsc) => {
          setSortBy(sortBy as keyof TableData);
          setSortAsc(sortAsc === "asc");
        }}
      >
        <Column flexGrow={0.3} align="center">
          <HeaderCell className="fw-bold text-black">
            <input
              type="checkbox"
              checked={isAllChecked}
              onChange={(ev) => selectAllRow(ev.target.checked)}
            />
          </HeaderCell>
          <Cell>{CellRowSelection}</Cell>
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Name">Patient</span>
          </HeaderCell>
          <TextCell component="Appointment" dataKey="patientName" />
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Appointed Caregiver"> Caregiver</span>
          </HeaderCell>
          <TextCell component="Appointment" dataKey="careGiverName" />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Start Date">Start Date</span>
          </HeaderCell>
          <Cell dataKey="appointmentStartDate">
            {(rowData: TableData) => (
              <span>
                {UtilityDate.formatHumanFriendlyDate(
                  new Date(rowData.appointmentStartDate)
                )}
              </span>
            )}
          </Cell>
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="End Date">End Date</span>
          </HeaderCell>
          <Cell dataKey="appointmentEndDate">
            {(rowData: TableData) => (
              <span>
                {UtilityDate.formatHumanFriendlyDate(
                  new Date(rowData.appointmentEndDate)
                )}
              </span>
            )}
          </Cell>
        </Column>

        <Column flexGrow={0.7} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Start Time">Start Time</span>
          </HeaderCell>
          <DateCell
            component="Appointment"
            dataKey="startTime"
            time
            previousTimeKey={"lastSavedStartTime"}
          />
        </Column>

        <Column flexGrow={0.6} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Max Hours">Max Hours</span>
          </HeaderCell>
          <TextCell component="Appointment" dataKey="maxHours" />
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span
              title="Auto Clock"
              className="click"
              onClick={() => {
                setSortBy("autoClockCheckIn");
                toggleFilterAutoClockCheckIn();
              }}
            >
              Auto Clock
              <BooleanToggleFilterIcon
                filterValue={
                  filterAutoClockCheckIn === 0
                    ? "false"
                    : filterAutoClockCheckIn === 1
                    ? "true"
                    : "default"
                }
                className="ms-2 toggle-filter-icon"
              />
            </span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) =>
              rowData.autoClockCheckIn ? (
                <Badge content="Enabled" className="p-1 px-2 bg-green-200" />
              ) : (
                <Badge content="Disabled" className="p-1 px-2" />
              )
            }
          </Cell>
        </Column>

        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Status">Status</span>
          </HeaderCell>
          <AppointmentStatusCell
            dataKey="status"
            alertDialog={alertDialogCtrl}
          />
        </Column>

        <Column flexGrow={0.8} sortable>
          <HeaderCell className="fw-bold text-black">
            <span className="d-inline-flex align-items-center">
              <FaCog className="me-2" /> Invoice Type
            </span>
          </HeaderCell>
          <Cell dataKey="invoiceGenerationInvokeType">
            {(rowData: TableData) => {
              const type = rowData.invoiceGenerationInvokeType;
              const getIcon = () => {
                switch (type) {
                  case "Daily":
                    return <FaRegCalendar className="text-primary" />;
                  case "Weekly":
                    return <FaCalendarWeek className="text-success" />;
                  case "Biweekly":
                    return <FaCalendarWeek className="text-warning" />;
                  case "Monthly":
                    return <FaRegCalendarAlt className="text-info" />;
                  default:
                    return <FaCog />;
                }
              };
              return (
                <div className="d-flex align-items-center">
                  {getIcon()}
                  <span className="ms-2">{type}</span>
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column flexGrow={0.7}>
          <HeaderCell className="fw-bold text-black">
            <span title="Task List">Task List</span>
          </HeaderCell>
          <Cell>
            {(rowData?: TableData) => (
              <SeeTasksCell taskDetailsList={rowData?.taskDetailsList} />
            )}
          </Cell>
        </Column>

        <Column flexGrow={1} align="center">
          <HeaderCell className="fw-bold text-black">
            <span title="Action">Action</span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) => (
              <div className="d-flex gap-2 justify-content-center">
                <button
                  className="btn btn-sm btn-outline-primary"
                  title="Edit Appointment"
                  onClick={() =>
                    handleSelectMenu("update", undefined, rowData.sKey)
                  }
                >
                  <FiEdit />
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  title="Delete Appointment"
                  onClick={() =>
                    handleSelectMenu("delete", undefined, rowData.sKey)
                  }
                >
                  <FiTrash2 />
                </button>
                <button
                  className="btn btn-sm btn-outline-success"
                  title="Generate Backdated Invoice"
                  onClick={() =>
                    rowData.sKey &&
                    routerHistory.push(
                      AppRouteUi.Appointment.BackdatedInvoiceGeneration(
                        rowData.sKey
                      )
                    )
                  }
                >
                  <FiFileText />
                </button>
              </div>
            )}
          </Cell>
        </Column>
      </Table>

      {data && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            total={filteredTableData.length}
            pages={Math.ceil(totalCount / pageSize)}
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page: any) =>
              dispatch(ActionsAppointmentTable.goToPage(page))
            }
          />
        </div>
      )}
    </div>
  );
};
